import { type ReactNode, useMemo } from 'react';
import type { ProgressSummary, ProgressBucket } from '@/types/progress';
import {
  StackedProgressBar,
  type BarStripe,
} from '@/components/ui/stacked-progress-bar';
import { assignCollectorColors } from '@/utils/collector-colors';
import type { ViewerContext } from '@/utils/member-display';
import { toSegments } from './progress-bars-helpers';

export type BarItemProps = {
  bucket: ProgressBucket;
  label: string;
  heightClass: string;
  stripes: BarStripe[];
  colors: Record<string, string>;
  viewer: ViewerContext;
};

export function BarItem({
  bucket,
  label,
  heightClass,
  stripes,
  colors,
  viewer,
}: BarItemProps): ReactNode {
  return (
    <StackedProgressBar
      label={label}
      segments={toSegments(bucket.by_collector, colors, viewer)}
      total={bucket.total}
      goal={bucket.goal}
      heightClass={heightClass}
      stripes={stripes}
    />
  );
}

export function useBarColors(data: ProgressSummary): Record<string, string> {
  const ids = useMemo(() => {
    const set = new Set<string>();
    data.weekly.by_collector.forEach((c) => set.add(c.id));
    data.total.by_collector.forEach((c) => set.add(c.id));
    return Array.from(set);
  }, [data]);
  const dbColors = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    data.weekly.by_collector.forEach((c) => {
      if (c.member_color) map[c.id] = c.member_color;
    });
    data.total.by_collector.forEach((c) => {
      if (c.member_color) map[c.id] = c.member_color;
    });
    return map;
  }, [data]);
  return useMemo(() => assignCollectorColors(ids, dbColors), [ids, dbColors]);
}
